import { useEffect, useState } from "react";
import type { Invoice } from "@/lib/api/types";
import { useTranslate } from "@/lib/i18n";
import { buildRefundReprint, refundEventsNewestFirst, refundSlipButtons, type RefundDocument } from "./lib/refundDocument";
import { refundPaperChoice } from "./lib/refundPaperChoice";
import { voidReasonLabelForCode } from "./lib/voidReasons";

/** Owns only the selected refund paper and its post-commit print request. */
export function useInvoiceRefundPrinting(invoice: Invoice | undefined) {
  const t = useTranslate();
  const [lastRefund, setLastRefund] = useState<RefundDocument | null>(null);
  const [reprintCommandId, setReprintCommandId] = useState<string | null>(null);
  // A counter lets a second tap on the same copy print again after DOM commit.
  const [refundPrintRequest, setRefundPrintRequest] = useState(0);

  /*
   * PRINTING THE REFUND SLIP, after React has committed the paper.
   *
   * The old shape called `window.print()` straight out of the click handler,
   * which worked only because the paper was a plain derivation of the bill and
   * was therefore ALREADY MOUNTED. A copy is per refund now: the click chooses
   * WHICH refund, so the document does not exist until the next render, and
   * printing in the same tick would put the previous slip — or none — on the
   * roll. That is a wrong tax document, not a cosmetic race.
   *
   * So the click bumps a counter and this observes it: an effect runs after
   * commit, one further frame lets the print stylesheet apply (the same shape
   * the cheque, the chit, the X and the Z all use), then it prints and restores
   * the scope. The scope is also cleared on unmount, so navigating away
   * mid-print cannot leave the whole app in `refund` print scope.
   *
   * Keyed on the COUNTER and not on the document, deliberately: tapping the
   * same refund's copy twice must produce two pieces of paper, and a `boolean`
   * or the document itself would make the second tap a no-op.
   */
  useEffect(() => {
    if (refundPrintRequest === 0) return;
    document.body.dataset.printScope = "refund";
    const frame = requestAnimationFrame(() => {
      try {
        window.print();
      } finally {
        delete document.body.dataset.printScope;
      }
    });
    return () => {
      cancelAnimationFrame(frame);
      delete document.body.dataset.printScope;
    };
  }, [refundPrintRequest]);

  const slips = invoice ? refundSlipButtons(invoice) : [];
  const events = invoice ? refundEventsNewestFirst(invoice) : [];
  const paperChoice = refundPaperChoice({
    reprintCommandId,
    originalCommandId: lastRefund?.commandId ?? null,
    knownCommandIds: events.map(refund => refund.commandId),
  });
  const target = paperChoice.kind === "COPY"
    ? events.find(refund => refund.commandId === paperChoice.commandId)
    : undefined;
  const paper = target && invoice ? buildRefundReprint({
    invoice,
    refund: target,
    reasonLabel: target.reasonCode ? voidReasonLabelForCode(t, target.reasonCode, "refund") : null,
  }) : paperChoice.kind === "ORIGINAL" ? lastRefund : null;

  function clear() {
    setLastRefund(null);
    setReprintCommandId(null);
  }
  function printOriginal(document: RefundDocument) {
    setReprintCommandId(null);
    setLastRefund(document);
    setRefundPrintRequest(request => request + 1);
  }
  function printCopy(commandId: string) {
    // An explicit copy supersedes a held original, even of this same refund.
    setLastRefund(null);
    setReprintCommandId(commandId);
    // A stale control must not print a blank sheet for an absent event.
    if (refundPaperChoice({ reprintCommandId: commandId, originalCommandId: null, knownCommandIds: events.map(refund => refund.commandId) }).kind === "NONE") return;
    setRefundPrintRequest(request => request + 1);
  }
  return { paper, slips, clear, printOriginal, printCopy };
}
